import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  type InvoiceMissing,
  type WorkItem,
  type WorkItemKind,
  type WorklistLoad,
  type WorklistSummary,
  type YearsResponse,
} from '@k-management/shared';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate, formatTimestamp } from '../../lib/format.js';
import { useListFilters } from '../../lib/list-filters.js';
import { HoldDialog, WORK_KIND_LABELS } from './HoldDialog.js';
import { reconcileWindowPath } from './ReconcilePanel.js';
import { useTreasuryChannel, worklistKeys } from './treasury-ui.js';

const TILES: { kind: WorkItemKind; title: string; unit: string }[] = [
  { kind: 'movimento', title: 'Banche', unit: 'movimenti' },
  { kind: 'spesa_carta', title: 'Carte', unit: 'spese' },
  { kind: 'fattura', title: 'Fatture', unit: 'da classificare' },
  { kind: 'rata', title: 'Rateazioni', unit: 'rate scadute' },
  { kind: 'autofattura', title: 'Autofatture', unit: 'da collegare' },
];

const STATE_FILTERS = {
  aperti: 'Da fare e sospesi',
  da_fare: 'Solo da fare',
  sospesi: 'Solo sospesi',
  scaduti: 'Sospesi scaduti',
} as const;

const MISSING_LABELS: Record<InvoiceMissing, string> = {
  anagrafica: 'senza anagrafica',
  voce: 'senza voce del piano',
  commessa: 'senza commessa',
};

type Filters = {
  year: string;
  state: keyof typeof STATE_FILTERS;
  kind: WorkItemKind | 'all';
};

/** The share of a load settled, held and still to do, as a bar. */
function LoadBar({ load }: { load: WorklistLoad }) {
  const width = (count: number) => `${(100 * count) / Math.max(load.total, 1)}%`;
  return (
    <span
      className="worklist-bar"
      title={`${load.settled} sistemati, ${load.held} sospesi, ${load.todo} da fare`}
    >
      <span className="ok" style={{ width: width(load.settled) }} />
      <span className="held" style={{ width: width(load.held) }} />
      <span className="todo" style={{ width: width(load.todo) }} />
    </span>
  );
}

function StateCell({ item }: { item: WorkItem }) {
  return (
    <>
      {item.state === 'da_fare' && <span className="pill bad">da fare</span>}
      {item.state === 'sospeso' && <span className="pill warn">sospeso</span>}
      {item.state === 'sospeso_scaduto' && (
        <span className="pill warn strong">sospeso scaduto</span>
      )}
      {item.hold && (
        <small className="muted block">
          {item.hold.reasonLabel}
          {item.hold.remindOn &&
            ` · ${item.state === 'sospeso_scaduto' ? 'era il' : 'ricordamelo il'} ${formatDate(item.hold.remindOn)}`}
          {item.hold.note && ` · ${item.hold.note}`}
        </small>
      )}
      {item.missing.length > 0 && (
        <small className="muted block">
          {item.missing.map((key) => MISSING_LABELS[key]).join(', ')}
        </small>
      )}
    </>
  );
}

/** Where an item is settled: the windows that exist already. */
function SettleAction({ item }: { item: WorkItem }) {
  const navigate = useNavigate();
  const settle = () => {
    if (item.kind === 'movimento') {
      window.open(
        reconcileWindowPath(item.link.accountCode!, item.id),
        '_blank',
        'popup,width=640,height=860',
      );
    } else if (item.kind === 'fattura') {
      void navigate(`/tesoreria/fatture/${item.id}`);
    } else if (item.kind === 'autofattura') {
      void navigate('/tesoreria/autofatture');
    } else if (item.kind === 'rata') {
      // Paid from the bank movement: the plan shows which (3.15).
      void navigate(`/tesoreria/rateazioni/${item.link.planId}`);
    } else {
      void navigate(`/tesoreria/carte?spesa=${encodeURIComponent(item.id)}&data=${item.date}`);
    }
  };
  return (
    <button type="button" className="primary small" onClick={settle}>
      Sistema
    </button>
  );
}

/**
 * Tesoreria › Da sistemare (3.00, docs/analisi/proposta-da-sistemare-chiusura-anno.md):
 * what came with the statements, the card files and the readings of Gestify
 * and is still to settle, by load, with the holds and their day to remind.
 */
export function WorklistPage() {
  usePageTitle('Da sistemare');
  const queryClient = useQueryClient();
  const canWrite = useCan('tesoreria.write');
  const [params, setParams] = useSearchParams();
  const load = params.get('lotto') ?? '';
  const { filters, setFilter, changedClass } = useListFilters<Filters>('worklist', {
    year: 'all',
    state: 'aperti',
    kind: 'all',
  });
  const [holding, setHolding] = useState<WorkItem | null>(null);
  useTreasuryChannel();

  const year = filters.year === 'all' ? '' : filters.year;
  const query = new URLSearchParams({
    state: filters.state,
    ...(filters.kind !== 'all' ? { kind: filters.kind } : {}),
    ...(year ? { year } : {}),
    ...(load ? { load } : {}),
  });
  const summary = useQuery({
    queryKey: worklistKeys.summary,
    queryFn: () => apiRequest<WorklistSummary>('/treasury/worklist/summary'),
  });
  const [allLoads, setAllLoads] = useState(false);
  const loads = useQuery({
    queryKey: [...worklistKeys.all, 'loads', year, allLoads],
    queryFn: () =>
      apiRequest<WorklistLoad[]>(
        `/treasury/worklist/loads?${new URLSearchParams({
          ...(year ? { year } : {}),
          ...(allLoads ? { all: 'true' } : {}),
        }).toString()}`,
      ),
  });
  const items = useQuery({
    queryKey: [...worklistKeys.all, 'items', query.toString()],
    queryFn: () => apiRequest<WorkItem[]>(`/treasury/worklist/items?${query.toString()}`),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: worklistKeys.all });
  const resume = useMutation({
    mutationFn: (holdId: string) =>
      apiRequest<void>(`/treasury/worklist/holds/${holdId}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  // Only the open years: a closed one has nothing left to settle (3.10).
  const openYears = useQuery({
    queryKey: ['treasury', 'year-closures'],
    queryFn: () => apiRequest<YearsResponse>('/treasury/year-closures'),
  });
  const years = (openYears.data?.years ?? [])
    .filter((item) => !item.closed)
    .map((item) => String(item.year))
    .reverse();
  const selected = loads.data?.find((item) => item.key === load) ?? null;
  const chooseLoad = (key: string) => setParams(key ? { lotto: key } : {});

  return (
    <section className="stack">
      <div className="row between">
        <p className="muted small">
          Ciò che è arrivato con estratti conto, file delle carte e letture di Gestify e non è
          ancora sistemato. Quello che non si può chiudere subito si mette in sospeso con il motivo
          e una data: dopo quella data torna in evidenza.
        </p>
        <div className="row">
          <label>
            Anno{' '}
            <select
              className={changedClass('year')}
              value={filters.year}
              onChange={(event) => setFilter('year', event.target.value)}
            >
              <option value="all">Tutti gli anni aperti</option>
              {years.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label>
            Stato{' '}
            <select
              className={changedClass('state')}
              value={filters.state}
              onChange={(event) => setFilter('state', event.target.value as Filters['state'])}
            >
              {Object.entries(STATE_FILTERS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {summary.error && <p className="error">{summary.error.message}</p>}
      {Boolean(summary.data?.closedYearChanges) && (
        <p className="notice small">
          Gestify ha {summary.data!.closedYearChanges} differenze su fatture di anni chiusi, non
          applicate: le trovi in <Link to="/tesoreria/chiusura-anno">Chiusura dell’anno</Link>.
        </p>
      )}
      {Boolean(summary.data?.scheduleToReschedule) && (
        <p className="notice small">
          Scadenze da riprogrammare: {summary.data!.scheduleToReschedule}. Le trovi nello{' '}
          <Link to="/tesoreria/scadenzario?blocco=da_riprogrammare">Scadenzario</Link>.
        </p>
      )}
      <div className="kpi-row">
        {TILES.filter((tile) => summary.data?.byKind[tile.kind]).map((tile) => {
          const counts = summary.data!.byKind[tile.kind]!;
          const active = filters.kind === tile.kind;
          return (
            <button
              key={tile.kind}
              type="button"
              className={`card kpi worklist-tile${active ? ' active' : ''}`}
              onClick={() => setFilter('kind', active ? 'all' : tile.kind)}
            >
              <span className="muted small">
                {tile.title} · {tile.unit}
              </span>
              <strong className={counts.todo ? 'error' : undefined}>{counts.todo} da fare</strong>
              <span className="small">
                {counts.held} sospesi{counts.late ? ` (${counts.late} scaduti)` : ''}
              </span>
              <span className="muted small">
                {counts.oldest
                  ? `il più vecchio: ${formatDate(counts.oldest)}`
                  : 'niente da sistemare'}
              </span>
            </button>
          );
        })}
      </div>

      <div className="card">
        <div className="row between">
          <h2>Caricamenti</h2>
          <label className="small">
            <input
              type="checkbox"
              checked={allLoads}
              onChange={(event) => setAllLoads(event.target.checked)}
            />{' '}
            Mostra anche i completati
          </label>
        </div>
        {loads.error && <p className="error">{loads.error.message}</p>}
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Caricato</th>
                <th>Lotto</th>
                <th>Periodo</th>
                <th className="num">Elementi</th>
                <th>Avanzamento</th>
                <th className="num">Restano</th>
              </tr>
            </thead>
            <tbody>
              {(loads.data ?? [])
                .filter((item) => filters.kind === 'all' || item.kind === filters.kind)
                .map((item) => (
                  <tr
                    key={item.key}
                    className={`clickable${item.key === load ? ' selected' : ''}`}
                    onClick={() => chooseLoad(item.key === load ? '' : item.key)}
                  >
                    <td>{formatTimestamp(item.loadedAt)}</td>
                    <td>{item.label}</td>
                    <td>
                      {item.from && item.to
                        ? `${formatDate(item.from)} – ${formatDate(item.to)}`
                        : '—'}
                    </td>
                    <td className="num">{item.total}</td>
                    <td>
                      <LoadBar load={item} />
                    </td>
                    <td className="num">
                      {item.todo + item.held === 0 ? (
                        <span className="pill ok">✓</span>
                      ) : (
                        <>
                          {item.todo}
                          {item.held > 0 && <span className="muted"> +{item.held} sospesi</span>}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <p className="muted small">
          Verde sistemati · giallo sospesi · rosso da fare. Le fatture lette prima della 3.00 sono
          raggruppate per giorno d’inserimento.
        </p>
      </div>

      <div className="card">
        <div className="row between">
          <h2>{selected ? selected.label : 'Elementi'}</h2>
          {load && (
            <button type="button" className="link" onClick={() => chooseLoad('')}>
              Tutti i caricamenti
            </button>
          )}
        </div>
        {items.error && <p className="error">{items.error.message}</p>}
        {resume.error && <p className="error">{resume.error.message}</p>}
        {items.data?.length === 0 && (
          <p className="muted">Niente da sistemare con questi filtri.</p>
        )}
        {Boolean(items.data?.length) && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Descrizione</th>
                  <th className="num">Importo</th>
                  <th>Stato</th>
                  <th className="num">Da quanto</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.data!.map((item) => (
                  <tr key={`${item.kind}:${item.id}`}>
                    <td>{formatDate(item.date)}</td>
                    <td>{WORK_KIND_LABELS[item.kind]}</td>
                    <td>{item.description}</td>
                    <td className="num">{formatMoney(item.amount)}</td>
                    <td className="worklist-state">
                      <StateCell item={item} />
                    </td>
                    <td className={`num${item.ageDays > 30 ? ' error' : ''}`}>{item.ageDays} gg</td>
                    <td className="num nowrap">
                      <SettleAction item={item} />{' '}
                      {canWrite && (
                        <button type="button" className="small" onClick={() => setHolding(item)}>
                          {item.hold ? 'Sposta data' : 'Sospendi'}
                        </button>
                      )}{' '}
                      {canWrite && item.hold && (
                        <button
                          type="button"
                          className="small"
                          disabled={resume.isPending}
                          onClick={() => resume.mutate(item.hold!.id)}
                        >
                          Riprendi
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {holding && (
        <HoldDialog
          item={holding}
          onClose={() => setHolding(null)}
          onSaved={async () => {
            setHolding(null);
            await refresh();
          }}
        />
      )}
    </section>
  );
}
